import React, { useEffect, useRef, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import ModalGlobal from "components/ModalGlobal/ModalGlobal";
import AvatarHeader from "./AvatarHeader";
import AvatarOptions from "./AvatarOptions";
import loadingImage from "../../../assets/images/loading.png";
import AvatarMessageSucess from "./AvatarMessageSucess";
import AvatarSelectMobile from "./AvatarSelectMobile";
import SystemContext from "context/SystemContext";
import {
  useUpdateUserAvatar,
  useUserAccountInfos,
} from "hooks/useUserInfos";
import { useAvatarProducts } from "hooks/useProductsRewards";

const AvatarSelect = ({
  open,
  onClose,
  onSelect,
  selectedAvatar,
  onCloseMenu,
}) => {
  const { setCurrentAvatar } = useContext(SystemContext);

  // ref usado para controlar o ModalGlobal (abrir o modal de loading, etc)
  const modalRef = useRef(null);

  const { data: currentUser } = useUserAccountInfos();
  const { data: avatarProducts } =
    useAvatarProducts(currentUser);

  // hook de tradução
  const { t } = useTranslation();

  // guarda a configuração dos modais (primeiro modal e modal de loading)
  const [modalConfigs, setModalConfigs] = useState(null);

  // guarda temporariamente o avatar selecionado antes de salvar
  const [chosenAvatar, setChosenAvatar] = useState(null);

  // controla qual tela do modal está aberta
  const [currentModal, setCurrentModal] = useState(0);

  // Responsividade
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { mutate: updateUserAvatar } = useUpdateUserAvatar();

  const handleSaveAvatar = () => {
    // só salva se o usuário realmente escolheu um avatar
    if (chosenAvatar !== null) {
      modalRef.current?.nextModal(1);
      setCurrentModal(1);

      const findAvatar = avatarProducts.find((item) => {
        return item.slug === chosenAvatar.slug;
      });

      updateUserAvatar(
        { rewardId: findAvatar?.reward_id, avatar: chosenAvatar.slug },
        {
          onSuccess: () => {
            onSelect(chosenAvatar);
            const newItem = {
              sub: currentUser.sub,
              avatar: chosenAvatar.slug,
            };

            setCurrentAvatar((prev) => [
              ...prev.filter((item) => item.sub !== currentUser.sub),
              newItem,
            ]);

            modalRef.current?.nextModal(2);
            // abre o modal de sucesso (índice 2)
            setCurrentModal(2);
          },
          onError: (erro) => {
            console.log("error", erro);
          },
        },
      );
    }
  };

  // Atualiza as configurações do modal sempre que ele estiver aberto
  // e houver mudança no avatar escolhido ou na lista de avatares
  useEffect(() => {
    if (open) {
      // toda vez que o modal abrir, gera as configurações atualizadas
      setModalConfigs(generateModalConfigs());
    }
  }, [open, chosenAvatar, avatarProducts]);

  // Quando o modal abrir, sincroniza o avatar salvo
  // com o estado local (chosenAvatar) para exibir corretamente na UI
  useEffect(() => {
    if (open && selectedAvatar && avatarProducts) {
      const findAvatar = avatarProducts?.find((item) => {
        return item.slug === selectedAvatar;
      });
      setChosenAvatar(findAvatar);
    }
  }, [open, selectedAvatar, avatarProducts]);

  // cria os dois modais (seleção de avatar e loading)
  const generateModalConfigs = () => [
    {
      title: "",
      modalStateOpen: true,
      size: "Large",

      // header com botão de fechar
      customComponent: (
        <AvatarHeader
          onClose={onClose}
          title={t("global.personalize_your_profile")}
        />
      ),

      // lista de avatares clicáveis
      content: (
        <AvatarOptions
          chosenAvatar={chosenAvatar}
          avatarList={avatarProducts}
          onSelect={(img) => setChosenAvatar(img)}
          modalRef={modalRef}
          onClose={onClose}
          onCloseMenu={onCloseMenu}
        />
      ),

      // botões do modal
      actions: [
        { type: "cancel", text: t("global.cancel"), callback: onClose },
        {
          type: "custom",
          text: t("global.save"),
          color: "primary",
          callback: handleSaveAvatar, //Salva avatar escolhido
          disabled: !chosenAvatar, //Desabilita o botão para prevenir que o usuário clique em "Salvar" sem ter escolhido um avatar.
        },
      ],
      actionsSx: {
        flexDirection: isMobile ? "column" : "row",
        "& .MuiButton-root": {
          width: "45%",
        },
      },
      scheme: ["customComponent", "content", "actions"],
    },

    // -------- MODAL DE PROCESSAMENTO --------
    {
      title: "",
      modalStateOpen: true,
      size: "Small",
      // header com botão de fechar
      customComponent: (
        <AvatarHeader onClose={onClose} title={t("global.processing_title")} />
      ),
      content: (
        <Box
          sx={{ marginTop: "50px", marginBottom: "30px", textAlign: "center" }}
        >
          <Typography>{t("global.change_your_profile_picture")}</Typography>
        </Box>
      ),

      media: {
        title: "processing",
        image: loadingImage,
        rotate: true,
        width: { xs: "50% !important", sm: "109px !important" },
        marginBottom: "30px",
      },
      cardStyle: { height: "468px" },

      scheme: ["customComponent", "content", "media"],
    },

    // -------- MODAL FINAL --------
    {
      title: "",
      modalStateOpen: true,
      size: "Small",
      // header com botão de fechar
      customComponent: (
        <AvatarHeader
          modalRef={modalRef}
          onClose={onClose}
          title={t("global.ready")}
        />
      ),
      content: <AvatarMessageSucess t={t} />,
      actions: [
        {
          type: "cancel",
          text: t("global.close"),
          callback: () => {
            onClose();
            modalRef.current?.nextModal(0);
            setCurrentModal(0);
          },
          sx: {
            background: "#FDFDFD",
            color: "#662D91",
            border: "2px solid #662D91",
            fontSize: "1rem",
            fontWeight: 500,
            textTransform: "uppercase",
          },
        },
      ],
      scheme: ["customComponent", "content", "actions"],
    },
  ];

  return (
    <>
      {isMobile ? (
        <>
          {currentModal === 0 && (
            <AvatarSelectMobile
              chosenAvatar={chosenAvatar}
              avatarList={avatarProducts}
              open={open}
              onClose={onClose}
              t={t}
              handleSaveAvatar={handleSaveAvatar}
              setChosenAvatar={setChosenAvatar}
              onCloseMenu={onCloseMenu}
              modalRef={modalRef}
            />
          )}
          {(currentModal === 1 || currentModal === 2) && (
            <ModalGlobal
              ref={modalRef}
              startModal={currentModal}
              openState={open}
              setOpenState={() => {
                onClose();
                setCurrentModal(0);
              }}
              modalConfigs={modalConfigs}
            />
          )}
        </>
      ) : (
        <ModalGlobal
          ref={modalRef}
          startModal={0}
          openState={open}
          setOpenState={onClose}
          modalConfigs={modalConfigs}
        />
      )}
    </>
  );
};

export default AvatarSelect;
